/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { set, action } from '@ember/object';
import FacebookLeadAdsForm from 'cio/models/facebook-lead-ads-form';
import { tracked } from '@glimmer/tracking';
import EnvironmentService from 'cio/services/environment';
import AuthService from 'cio/services/auth';

interface FormsFacebookLeadAdsFormSettingsAddAttributesArgs {
  form: FacebookLeadAdsForm;
  isDisabled?: boolean;
  isDetailsPage?: boolean;
}

export default class FormsFacebookLeadAdsFormSettingsAddAttributes extends Component<FormsFacebookLeadAdsFormSettingsAddAttributesArgs> {
  @service auth!: AuthService;
  @service environment!: EnvironmentService;

  @tracked extraAttrsArray: { key: string; value: string; renderKey: string }[] = [];

  get selectableAttributes() {
    return this.environment.sentAttributes.without('id');
  }

  // don't display the table at all if there are no rows, not even an empty one:
  get shouldShow() {
    return this.extraAttrsArray && Object.keys(this.extraAttrsArray).length > 0;
  }

  get canChangeAttributes() {
    if (this.args.form.get('isNew')) {
      return this.auth.hasPermission('journeys/forms/create');
    }

    return this.auth.hasPermission('journeys/forms/edit');
  }

  @action
  initializeExtraAttrs() {
    // initialize extraAttrs on args form if empty:
    if (!this.args.form.extraAttrs) {
      set(this.args.form, 'extraAttrs', {});
    }

    this.extraAttrsArray = Object.entries(this.args.form.extraAttrs).map(([key, value]) => ({
      key,
      value,
      renderKey: window.UUID.generate(),
    }));
  }

  @action updateName(index: number, key: string) {
    let { value, renderKey } = this.extraAttrsArray[index];

    let vals = [...this.extraAttrsArray];
    vals[index] = { key: key.trim(), value, renderKey };
    this.extraAttrsArray = vals;
    this.onChange();
  }

  @action updateValue(index: number, value: string) {
    let { key, renderKey } = this.extraAttrsArray[index];

    let vals = [...this.extraAttrsArray];
    vals[index] = { key, value: value.trim(), renderKey };
    this.extraAttrsArray = vals;
    this.onChange();
  }

  @action removeAttr(index: number) {
    let attrs = [...this.extraAttrsArray];
    attrs.removeAt(index, 1);
    this.extraAttrsArray = attrs;

    this.onChange();
  }

  @action addAttr() {
    this.extraAttrsArray = [...this.extraAttrsArray, { key: '', value: '', renderKey: window.UUID.generate() }];
  }

  onChange() {
    let extraAttrs = this.extraAttrsArray
      .map(({ key, value }) => ({ key: key.trim(), value })) // trim leading/trailing whitespace from attribute names before saving
      .filter((entry) => entry.key) // don't save attrs with empty attr names
      .reduce(
        (aggr: Record<string, string>, entry: { key: string; value: string }) => ({
          ...aggr,
          [entry.key]: entry.value,
        }),
        {},
      );

    this.args.form.set('extraAttrs', extraAttrs);
  }
}
